
import React, { useCallback, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import { ComparisonStatus, RepairEstimate, EstimateSummary } from '@/types/quick-compare';
import { Upload, FileText, Check } from 'lucide-react';
import { Progress } from '@/components/ui/progress';
import { toast } from '@/hooks/use-toast';
import { extractTextFromPDF, processEstimate } from '@/utils/estimates/pdfProcessor';

interface FileUploaderProps {
  title: string;
  description: string;
  onFileProcessed: (estimate: RepairEstimate) => void;
  status: ComparisonStatus;
  setStatus: React.Dispatch<React.SetStateAction<ComparisonStatus>>;
  isUploaded: boolean;
}

export const FileUploader: React.FC<FileUploaderProps> = ({
  title,
  description,
  onFileProcessed,
  status,
  setStatus,
  isUploaded
}) => {
  const [progress, setProgress] = useState(0);
  const [fileName, setFileName] = useState<string | null>(null);

  const processFile = async (file: File) => {
    try {
      // Start the upload process
      setStatus('uploading');
      setFileName(file.name);
      
      // Simulate upload progress
      const progressInterval = setInterval(() => {
        setProgress((prev) => {
          if (prev >= 90) {
            clearInterval(progressInterval);
            return 90;
          }
          return prev + 10;
        });
      }, 200);

      // Extract text from PDF
      setProgress(95);
      clearInterval(progressInterval);
      setStatus('parsing');
      
      console.log(`Processing file: ${file.name} (${Math.round(file.size / 1024)}KB)`);
      
      // Extract text from the PDF
      const extractedText = await extractTextFromPDF(file);
      console.log(`Text extraction complete - ${extractedText.length} characters extracted`);
      
      // Process the extracted text into structured data
      const estimateResult = await processEstimate(file);
      console.log('Estimate processing complete:', estimateResult);
      
      // Log the repair sections to see if part types are included
      if (estimateResult.data.repairSections) {
        console.log('Sample repair section items:', 
          estimateResult.data.repairSections.slice(0, 3).map(r => ({ 
            desc: r.description, 
            partType: r.partType,
            laborType: r.laborType,
            paintTime: r.paintTime
          }))
        );
      }
      
      // Convert to RepairEstimate format for comparison
      const repairEstimate: RepairEstimate = {
        documentId: Math.random().toString(36).substring(2, 9),
        fileName: file.name,
        vehicleInfo: {
          vin: estimateResult.data.vehicle?.vin || '',
          year: Number(estimateResult.data.vehicle?.year || 0),
          make: estimateResult.data.vehicle?.make || '',
          model: estimateResult.data.vehicle?.model || '',
        },
        totalAmount: estimateResult.calculatedTotal || 0,
        sections: {},
        metadata: {
          roNumber: estimateResult.data.repairInfo?.roNumber || null
        }
      };

      // Process repair sections into the required format
      let totalAmount = 0;
      let lineItemCount = 0;
      
      // Group repair operations by section
      if (estimateResult.data.repairSections && estimateResult.data.repairSections.length > 0) {
        // Step 1: Group operations by section
        const sectionMap: Record<string, any[]> = {};
        estimateResult.data.repairSections.forEach(repair => {
          const sectionName = repair.section || 'General';
          if (!sectionMap[sectionName]) {
            sectionMap[sectionName] = [];
          }
          sectionMap[sectionName].push(repair);
        });
        
        // Step 2: Process each section
        Object.entries(sectionMap).forEach(([sectionName, repairs]) => {
          if (!repairEstimate.sections[sectionName]) {
            repairEstimate.sections[sectionName] = [];
          }
          
          repairs.forEach(repair => {
            const price = repair.price || 0;
            const quantity = repair.quantity || 1;
            const lineItemTotal = price * quantity;
            totalAmount += lineItemTotal;
            lineItemCount++;
            
            const lineItem = {
              description: repair.description,
              quantity: quantity,
              price: price,
              totalPrice: lineItemTotal,
              laborHours: repair.paintTime || 0,
              operationCode: repair.operationCode || '',
              laborType: repair.laborType || '',
              partType: repair.partType || '',
              lineNumber: repair.lineNumber
            };
            
            repairEstimate.sections[sectionName].push(lineItem);
          });
        });
      }
      
      // Add summary information if available
      if (estimateResult.data.summary) {
        const summaryData = estimateResult.data.summary;
        
        // Convert the flat summary structure to the nested structure expected by RepairEstimate
        repairEstimate.summary = {
          partsTotal: summaryData.partsTotal || 0,
          labor: {
            body: summaryData.labor.body || 0,
            bodyHours: summaryData.labor.bodyHours || 0,
            refinish: summaryData.labor.refinish || 0,
            refinishHours: summaryData.labor.refinishHours || 0,
            mechanical: summaryData.labor.mechanical || 0,
            mechanicalHours: summaryData.labor.mechanicalHours || 0,
            rate: summaryData.labor.rate || 0
          },
          materials: {
            paint: summaryData.materials.paint || 0,
            other: summaryData.materials.other || 0
          },
          subtotal: summaryData.subtotal || 0,
          salesTax: summaryData.salesTax || 0,
          grandTotal: summaryData.grandTotal || totalAmount,
          insuranceTotal: summaryData.insuranceTotal || 0,
          customerTotal: summaryData.customerTotal || 0
        };
        
        // Log labor hours for debugging
        console.log('Labor hours from summary:', {
          bodyHours: repairEstimate.summary.labor.bodyHours,
          refinishHours: repairEstimate.summary.labor.refinishHours,
          mechanicalHours: repairEstimate.summary.labor.mechanicalHours,
          total: (repairEstimate.summary.labor.bodyHours || 0) + 
                (repairEstimate.summary.labor.refinishHours || 0) + 
                (repairEstimate.summary.labor.mechanicalHours || 0)
        });
        
        // Use the extracted grand total if available
        if (summaryData.grandTotal && summaryData.grandTotal > 0) {
          totalAmount = summaryData.grandTotal;
          repairEstimate.totalAmount = totalAmount;
        }
      }
      
      // Use the calculated total
      repairEstimate.totalAmount = totalAmount;
      
      console.log(`Estimate processed - Total amount: $${totalAmount.toFixed(2)}`);
      console.log('Sections:', Object.keys(repairEstimate.sections));
      console.log('Total line items:', lineItemCount);

      // If we have missing data, provide a fallback
      if (Object.keys(repairEstimate.sections).length === 0) {
        // Add some fallback data to prevent errors
        repairEstimate.sections = {
          'Body': [
            { 
              description: 'Extracted from PDF - No structured data found',
              quantity: 1,
              price: 0,
              totalPrice: 0,
              laborHours: 0
            }
          ]
        };
        
        toast("Extraction Error: We couldn't extract structured data from this PDF. Basic comparison will be available.");
      } else {
        // Show a detailed toast with extraction results
        const vehicleInfo = repairEstimate.vehicleInfo;
        const vehicleText = vehicleInfo.year && vehicleInfo.make ? 
          `${vehicleInfo.year} ${vehicleInfo.make} ${vehicleInfo.model}` : 
          'Vehicle info extracted';
          
        toast(`PDF Processed Successfully: ${lineItemCount} line items extracted with total $${totalAmount.toFixed(2)}. ${vehicleText}${estimateResult.data.repairInfo?.roNumber ? ` (RO #${estimateResult.data.repairInfo.roNumber})` : ''}`);
      }

      setProgress(100);
      onFileProcessed(repairEstimate);
      setStatus('idle');
      
    } catch (error) {
      console.error('Error processing file:', error);
      toast(`Processing Error: ${error instanceof Error ? error.message : "Failed to process the PDF file"}`);
      setProgress(0);
      setStatus('error');
    }
  };

  const onDrop = useCallback((acceptedFiles: File[]) => {
    const file = acceptedFiles[0];
    if (file) {
      setFileName(file.name);
      setProgress(0);
      processFile(file);
    }
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'application/pdf': ['.pdf']
    },
    maxFiles: 1,
    disabled: status === 'uploading' || status === 'parsing' || isUploaded
  });

  return (
    <div className="border rounded-lg p-6 bg-card">
      <h2 className="font-semibold text-xl mb-2">{title}</h2>
      <p className="text-muted-foreground text-sm mb-4">{description}</p>
      
      {!isUploaded ? (
        <div
          {...getRootProps()}
          className={`border-2 border-dashed rounded-md p-8 transition-colors text-center cursor-pointer
            ${isDragActive ? 'border-primary bg-primary/5' : 'border-muted-foreground/20 hover:border-primary/50'}`}
        >
          <input {...getInputProps()} />
          <div className="flex flex-col items-center justify-center space-y-2">
            <Upload className="h-10 w-10 text-muted-foreground" />
            <p className="text-sm font-medium">
              {isDragActive ? 'Drop the PDF here' : 'Drag & drop a PDF file or click to browse'}
            </p>
            <p className="text-xs text-muted-foreground">PDF files only, max 10MB</p>
          </div>
        </div>
      ) : (
        <div className="border rounded-md p-4 bg-background/50">
          <div className="flex items-center space-x-3">
            <div className="bg-green-500/20 p-2 rounded-full">
              <Check className="h-5 w-5 text-green-600" />
            </div>
            <div className="flex-1">
              <p className="font-medium text-sm">{fileName}</p>
              <p className="text-xs text-muted-foreground">Uploaded successfully</p>
            </div>
            <FileText className="h-5 w-5 text-muted-foreground" />
          </div>
        </div>
      )}

      {(status === 'uploading' || status === 'parsing') && fileName && !isUploaded && (
        <div className="mt-4 space-y-2">
          <div className="flex items-center justify-between text-sm">
            <span>{status === 'uploading' ? 'Uploading...' : 'Processing PDF...'}</span>
            <span>{Math.round(progress)}%</span>
          </div>
          <Progress value={progress} className="h-2" />
          {status === 'parsing' && progress >= 95 && (
            <p className="text-xs text-muted-foreground mt-1">
              Extracting and analyzing repair line items... (this may take a moment)
            </p>
          )}
        </div>
      )}
    </div>
  );
};
